/**
 * /bill — 账单流水
 *
 * 设计稿: _design_reference/mini-bill.html
 *
 * 功能:
 * 1. filter tab 筛选栏 (全部/充值/消费/退款/奖励)
 * 2. KPI 统计条 (本月支出/充值/退款) — 从当页记录派生
 * 3. 按周分组带 icon 的流水列表
 * 4. 三态: booting skeleton / signed-out gate / signed-in content
 */
import { useBillList } from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth } from "../lib/auth";
import { miniDisplayCurrency, miniDisplayLocale } from "../lib/locale-currency";

// ─── Filter tab config ────────────────────────────────────────────────────────
type FilterKey = "all" | "recharge" | "consume" | "refund" | "reward";

// Map filter key → backend changeType param values (empty = all)
// The backend does server-side dict localization so we pass raw type codes.
const FILTER_CHANGE_TYPE: Record<FilterKey, string | undefined> = {
  all: undefined,
  recharge: "RECHARGE",
  consume: "CONSUME",
  refund: "REFUND",
  reward: "REWARD",
};

// ─── Bill icon helper ─────────────────────────────────────────────────────────
// Maps the bill type string (from backend, server-locale) to an icon + bg
interface BillIconStyle {
  icon: string;
  bg: string;
  color: string | undefined;
}

function resolveBillIcon(type: string, changeRubKopecks: number): BillIconStyle {
  const t = type.toLowerCase();
  // Refund / return
  if (t.includes("refund") || t.includes("退款") || t.includes("возврат")) {
    return { icon: "↺", bg: "var(--color-secondary-soft)", color: "var(--color-secondary)" };
  }
  // Recharge / top-up
  if (t.includes("recharge") || t.includes("充值") || t.includes("пополн")) {
    return { icon: "💳", bg: "var(--color-primary-soft)", color: "var(--color-primary-fill)" };
  }
  // Reward / bonus
  if (t.includes("reward") || t.includes("奖励") || t.includes("бонус") || t.includes("★")) {
    return {
      icon: "⭐",
      bg: "var(--color-sticker-yellow-soft)",
      color: "var(--color-sticker-yellow-fg)",
    };
  }
  // Shipping / logistics
  if (t.includes("运费") || t.includes("доставк") || t.includes("logistics")) {
    return { icon: "✈", bg: "var(--color-surface)", color: undefined };
  }
  // Consume / order payment — or any debit
  if (t.includes("consume") || t.includes("消费") || t.includes("расход") || changeRubKopecks < 0) {
    return { icon: "🛍", bg: "var(--color-surface)", color: undefined };
  }
  // Default credit
  return { icon: "💰", bg: "var(--color-primary-soft)", color: "var(--color-primary-fill)" };
}

// ─── Week grouping ────────────────────────────────────────────────────────────
interface WeekGroup {
  label: string;
  records: import("@kupikupi/api").BillRecord[];
}

function getISOWeekKey(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "unknown";
  // ISO week: Monday-based, locale-neutral
  const tmp = new Date(d.getTime());
  tmp.setHours(0, 0, 0, 0);
  tmp.setDate(tmp.getDate() + 3 - ((tmp.getDay() + 6) % 7));
  const week1 = new Date(tmp.getFullYear(), 0, 4);
  const weekNo = Math.round(
    ((tmp.getTime() - week1.getTime()) / 86400000 + 1) / 7,
  );
  return `${tmp.getFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function weekLabel(weekKey: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  const now = new Date();
  const currentKey = getISOWeekKey(now.toISOString());
  const lastKey = getISOWeekKey(new Date(now.getTime() - 7 * 86400000).toISOString());
  if (weekKey === currentKey) return t("week.current");
  if (weekKey === lastKey) return t("week.last");
  // Format as week range Mon–Sun
  const parts = weekKey.split("-W");
  const year = Number.parseInt(parts[0] ?? "2000", 10);
  const week = Number.parseInt(parts[1] ?? "1", 10);
  // Monday of that ISO week
  const jan4 = new Date(year, 0, 4);
  const monday = new Date(
    jan4.getTime() + ((week - 1) * 7 - ((jan4.getDay() + 6) % 7)) * 86400000,
  );
  const sunday = new Date(monday.getTime() + 6 * 86400000);
  const fmt = (d: Date) => `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
  return t("week.label", { range: `${fmt(monday)}–${fmt(sunday)}` });
}

function groupByWeek(
  records: import("@kupikupi/api").BillRecord[],
  t: (k: string, opts?: Record<string, unknown>) => string,
): WeekGroup[] {
  const map = new Map<string, import("@kupikupi/api").BillRecord[]>();
  for (const r of records) {
    const key = getISOWeekKey(r.changeTime);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  // Sort descending (most recent first)
  const sorted = [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  return sorted.map(([key, recs]) => ({ label: weekLabel(key, t), records: recs }));
}

// ─── KPI strip: derived from current page records ────────────────────────────
function deriveKpi(
  records: import("@kupikupi/api").BillRecord[],
): { spent: number; recharged: number; refunded: number } {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  let spent = 0;
  let recharged = 0;
  let refunded = 0;
  for (const r of records) {
    const d = new Date(r.changeTime);
    if (d < monthStart) continue;
    if (r.changeRubKopecks < 0) spent += Math.abs(r.changeRubKopecks);
    else {
      const t = r.type.toLowerCase();
      if (t.includes("refund") || t.includes("退款") || t.includes("возврат")) {
        refunded += r.changeRubKopecks;
      } else {
        recharged += r.changeRubKopecks;
      }
    }
  }
  return { spent, recharged, refunded };
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────
function BillSkeleton() {
  return (
    <div style={{ padding: "0 12px", display: "flex", flexDirection: "column", gap: 8 }}>
      {/* KPI strip skeleton */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, padding: "12px 0 4px" }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 10,
              padding: "10px 8px",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <TgSkeleton height={18} width="70%" />
            <TgSkeleton height={10} width="90%" />
          </div>
        ))}
      </div>
      {/* Row skeletons */}
      <div
        style={{
          background: "#fff",
          border: "1px solid var(--color-border)",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              padding: "10px 12px",
              display: "grid",
              gridTemplateColumns: "32px 1fr auto",
              gap: 10,
              alignItems: "center",
              borderBottom: i < 3 ? "1px solid var(--color-border)" : "none",
            }}
          >
            <TgSkeleton height={32} width={32} radius={8} />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <TgSkeleton height={11} width="70%" />
              <TgSkeleton height={9} width="50%" />
            </div>
            <TgSkeleton height={14} width={60} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export function BillPage() {
  const { t, i18n } = useTranslation("bill");
  const displayLocale = miniDisplayLocale(i18n.language);
  const displayCurrency = miniDisplayCurrency(i18n.language) as "RUB" | "CNY";
  const auth = useAuth();

  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");

  const changeType = FILTER_CHANGE_TYPE[activeFilter];

  const { data, isLoading } = useBillList(
    auth.status === "signed-in" ? auth.token : null,
    {
      pageNo: 1,
      pageSize: 50,
      ...(changeType ? { changeType } : {}),
    },
  );

  const records = data?.records ?? [];

  const kpi = useMemo(() => deriveKpi(records), [records]);

  const weekGroups = useMemo(() => groupByWeek(records, t as (k: string, opts?: Record<string, unknown>) => string), [records, t]);

  const FILTERS: { key: FilterKey; label: string }[] = [
    { key: "all", label: t("filter.all") },
    { key: "recharge", label: t("filter.recharge") },
    { key: "consume", label: t("filter.consume") },
    { key: "refund", label: t("filter.refund") },
    { key: "reward", label: t("filter.reward") },
  ];

  // ── Three-state rendering ────────────────────────────────────────────────
  const showSkeleton = auth.status === "booting" || (auth.status === "signed-in" && isLoading);
  const showSignin = auth.status === "signed-out";

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.title")} back />

      {/* Filter tab bar — always visible */}
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "8px 12px",
          background: "#fff",
          borderBottom: "1px solid var(--color-border)",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}
      >
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveFilter(key)}
            style={{
              background: activeFilter === key ? "var(--color-text)" : "transparent",
              color: activeFilter === key ? "#fff" : "var(--color-text-muted)",
              padding: "6px 12px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 700,
              border: 0,
              whiteSpace: "nowrap",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Skeleton state */}
      {showSkeleton && <BillSkeleton />}

      {/* Sign-in gate */}
      {showSignin && (
        <div className="tg-body" style={{ flex: 1 }}>
          <TgEmpty icon="🔒" title={t("state.signin_t")} hint={t("state.signin_s")} />
        </div>
      )}

      {/* Signed-in: actual content */}
      {!showSkeleton && !showSignin && (
        <div className="tg-body" style={{ flex: 1, paddingTop: 0 }}>
          {/* KPI strip */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 6,
              padding: 12,
            }}
          >
            {/* Spent */}
            <div
              style={{
                background: "linear-gradient(180deg,var(--color-cta-soft),#fff)",
                border: "1px solid rgba(255,107,74,.2)",
                borderRadius: 10,
                padding: "10px 8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: "-apple-system,system-ui,sans-serif",
                  fontSize: 14,
                  fontWeight: 900,
                  color: "var(--color-cta)",
                }}
              >
                {kpi.spent > 0
                  ? formatPrice(kpi.spent, displayLocale, displayCurrency)
                      .replace(/[₽¥]/g, "")
                      .trim()
                  : "—"}
              </div>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                {t("kpi.spent")}
              </div>
            </div>

            {/* Recharged */}
            <div
              style={{
                background: "linear-gradient(180deg,var(--color-primary-soft),#fff)",
                border: "1px solid rgba(11,168,136,.2)",
                borderRadius: 10,
                padding: "10px 8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: "-apple-system,system-ui,sans-serif",
                  fontSize: 14,
                  fontWeight: 900,
                  color: "var(--color-primary-fill)",
                }}
              >
                {kpi.recharged > 0
                  ? formatPrice(kpi.recharged, displayLocale, displayCurrency)
                      .replace(/[₽¥]/g, "")
                      .trim()
                  : "—"}
              </div>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                {t("kpi.recharged")}
              </div>
            </div>

            {/* Refunded */}
            <div
              style={{
                background: "linear-gradient(180deg,var(--color-secondary-soft),#fff)",
                border: "1px solid rgba(107,78,255,.2)",
                borderRadius: 10,
                padding: "10px 8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: "-apple-system,system-ui,sans-serif",
                  fontSize: 14,
                  fontWeight: 900,
                  color: "var(--color-secondary)",
                }}
              >
                {kpi.refunded > 0
                  ? formatPrice(kpi.refunded, displayLocale, displayCurrency)
                      .replace(/[₽¥]/g, "")
                      .trim()
                  : "—"}
              </div>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                {t("kpi.refunded")}
              </div>
            </div>
          </div>

          {/* Empty state */}
          {records.length === 0 && (
            <TgEmpty icon="🧾" title={t("empty.title")} hint={t("empty.hint")} />
          )}

          {/* Week-grouped list */}
          {weekGroups.map((group) => (
            <div key={group.label}>
              {/* Week header */}
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 10,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--color-text-muted)",
                  fontWeight: 700,
                  padding: "6px 2px",
                }}
              >
                {group.label}
              </div>

              {/* Week card */}
              <div
                style={{
                  background: "#fff",
                  borderRadius: 10,
                  overflow: "hidden",
                  border: "1px solid var(--color-border)",
                }}
              >
                {group.records.map((r, idx) => {
                  const { icon, bg, color } = resolveBillIcon(r.type, r.changeRubKopecks);
                  const isLast = idx === group.records.length - 1;
                  const isPositive = r.changeRubKopecks >= 0;
                  const amountStr = formatPrice(
                    Math.abs(r.changeRubKopecks),
                    displayLocale,
                    displayCurrency,
                  );
                  return (
                    <div
                      key={r.id}
                      style={{
                        padding: "10px 12px",
                        display: "grid",
                        gridTemplateColumns: "32px 1fr auto",
                        gap: 10,
                        alignItems: "center",
                        borderBottom: isLast ? "none" : "1px solid var(--color-border)",
                      }}
                    >
                      {/* Icon cell */}
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: bg,
                          color: color ?? "inherit",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        {icon}
                      </div>

                      {/* Text cell */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600 }}>
                          {r.type || "—"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: "var(--color-text-muted)",
                            fontFamily: "monospace",
                            marginTop: 2,
                          }}
                        >
                          {r.remark
                            ? r.remark
                            : r.orderNo
                              ? `№ ${r.orderNo}`
                              : r.changeTime}
                        </div>
                      </div>

                      {/* Amount cell */}
                      <div
                        style={{
                          fontFamily: "-apple-system,system-ui,sans-serif",
                          fontSize: 12,
                          fontWeight: 900,
                          color: isPositive ? "var(--color-primary-fill)" : "inherit",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isPositive ? `+ ${amountStr}` : `− ${amountStr}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </TgPage>
  );
}
